/**
 * EvaluatorPanel.tsx
 *
 * AI-powered arc critique panel, shown inside StepArcReview below the slot cards.
 *
 * Two feedback channels:
 *   1. Structural suggestions (per slot) — prefilled textarea; user accepts/modifies/dismisses.
 *      These become "accepted evaluation feedback" in the regeneration prompt.
 *
 *   2. Enrichment prompt — Gemini's bulleted questions asking the author for raw material
 *      (personal stories, metrics, client results). User types into ONE auto-expanding
 *      optional text box. Appended to the original brief as "additional context from author."
 *
 * The two channels stay separate in the regeneration request and are never mixed.
 *
 * IMPORTANT: All sub-components (AutoTextarea, VerdictChip, QualityChip, SuggestionRow)
 * are defined at MODULE scope, not inside EvaluatorPanel. Defining components inside a
 * render function gives React a new type reference on every render → full remount on
 * every state update → scroll-to-top and lost focus on Accept/Dismiss clicks.
 */

import React, { useState, useRef, useEffect } from "react";
import { ACCENT, card, primaryBtn, secondaryBtn, spinnerStyle } from "../DistillPageV2/sharedStyles";
import type { EvaluationResult, SlotEvaluation } from "./types";
import { QUALITY_COLORS, ROLE_LABELS } from "./types";
import type { ArcSlot } from "./types";

// ── Auto-expanding textarea ───────────────────────────────────────────────────

const AutoTextarea: React.FC<{
  value:       string;
  onChange:    (v: string) => void;
  placeholder: string;
  minRows?:    number;
  style?:      React.CSSProperties;
}> = ({ value, onChange, placeholder, minRows = 2, style }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={minRows}
      style={{
        width:        '100%',
        border:       '1.5px solid #E4E4E0',
        borderRadius: '8px',
        padding:      '10px 12px',
        fontSize:     '13px',
        fontFamily:   'inherit',
        color:        '#333',
        background:   '#FAFAFA',
        outline:      'none',
        resize:       'none',
        overflow:     'hidden',
        lineHeight:   '1.55',
        boxSizing:    'border-box',
        transition:   'border-color 0.15s',
        ...style,
      }}
      onFocus={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.background = '#fff'; }}
      onBlur={e  => { e.currentTarget.style.borderColor = '#E4E4E0'; e.currentTarget.style.background = '#FAFAFA'; }}
    />
  );
};

// ── Verdict chip ─────────────────────────────────────────────────────────────

const VerdictChip: React.FC<{ verdict: 'strong' | 'thin' | 'weak' }> = ({ verdict }) => {
  const color  = QUALITY_COLORS[verdict];
  const labels = { strong: '✓ Strong Arc', thin: '~ Needs Enrichment', weak: '⚠ Weak Arc' };
  return (
    <span style={{
      display:       'inline-flex',
      alignItems:    'center',
      padding:       '4px 12px',
      borderRadius:  '20px',
      background:    `${color}14`,
      border:        `1.5px solid ${color}44`,
      color,
      fontSize:      '12px',
      fontWeight:    700,
      letterSpacing: '0.02em',
    }}>
      {labels[verdict]}
    </span>
  );
};

// ── Quality chip ──────────────────────────────────────────────────────────────

const QualityChip: React.FC<{ quality: 'strong' | 'thin' | 'weak' }> = ({ quality }) => {
  const color  = QUALITY_COLORS[quality];
  const labels = { strong: 'Strong', thin: 'Thin', weak: 'Weak' };
  return (
    <span style={{
      padding:       '2px 8px',
      borderRadius:  '4px',
      background:    `${color}14`,
      border:        `1px solid ${color}44`,
      color,
      fontSize:      '10px',
      fontWeight:    700,
      letterSpacing: '0.04em',
      flexShrink:    0,
    }}>
      {labels[quality].toUpperCase()}
    </span>
  );
};

// ── Suggestion row ────────────────────────────────────────────────────────────
// Module-scope so React sees a stable component type across EvaluatorPanel renders.

interface SuggestionRowProps {
  label:            string;
  quality?:         'strong' | 'thin' | 'weak';
  critique:         string;
  suggestion:       string;   // initial text — prefilled in the editable textarea
  isAccepted:       boolean;
  isDismissed:      boolean;
  onAccept:         (text: string) => void;
  onUndoAccept:     () => void;
  onDismiss:        () => void;
  onUndoDismiss:    () => void;
  onUpdateAccepted: (text: string) => void;
}

const SuggestionRow: React.FC<SuggestionRowProps> = ({
  label, quality, critique, suggestion,
  isAccepted, isDismissed,
  onAccept, onUndoAccept, onDismiss, onUndoDismiss, onUpdateAccepted,
}) => {
  // Local draft tracks the user's edits to the suggestion text.
  // When accepted, changes are mirrored to the parent's accepted Map via onUpdateAccepted.
  const [draft, setDraft] = useState(suggestion);

  const handleChange = (v: string) => {
    setDraft(v);
    if (isAccepted) onUpdateAccepted(v);
  };

  if (isDismissed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.4 }}>
        <span style={{ fontSize: '12px', color: '#999', flex: 1 }}>{label} — dismissed</span>
        <button
          onClick={onUndoDismiss}
          style={{ background: 'none', border: 'none', fontSize: '11px', color: ACCENT, cursor: 'pointer', padding: 0 }}
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background:    '#FAFAFA',
      border:        '1.5px solid #EBEBEB',
      borderRadius:  '10px',
      padding:       '14px 16px',
      display:       'flex',
      flexDirection: 'column',
      gap:           '10px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>{label}</span>
        {quality && <QualityChip quality={quality} />}
      </div>

      {/* Critique */}
      <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.55' }}>{critique}</div>

      {/* Editable suggestion textarea */}
      <div>
        <div style={{
          fontSize:      '10px',
          fontWeight:    700,
          color:         '#bbb',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom:  '6px',
        }}>
          Suggested fix{isAccepted ? ' ✓ accepted' : ''}
        </div>
        <AutoTextarea
          value={draft}
          onChange={handleChange}
          placeholder="Evaluator suggestion…"
          minRows={2}
          style={isAccepted ? { borderColor: '#2DB88F', background: '#F0FBF7' } : {}}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {isAccepted ? (
          <button
            onClick={onUndoAccept}
            style={{ ...secondaryBtn, fontSize: '12px', padding: '6px 14px', color: '#2DB88F', border: '1.5px solid #2DB88F44', background: '#F0FBF7' }}
          >
            ✓ Accepted — undo
          </button>
        ) : (
          <button
            onClick={() => onAccept(draft)}
            style={{ ...secondaryBtn, fontSize: '12px', padding: '6px 14px', color: '#2DB88F', border: '1.5px solid #2DB88F44', background: '#F0FBF7' }}
          >
            ✓ Accept
          </button>
        )}
        <button
          onClick={onDismiss}
          style={{ ...secondaryBtn, fontSize: '12px', padding: '6px 14px', color: '#999', border: '1.5px solid #E8E8E8', background: '#fff' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  arc:          ArcSlot[];
  evaluation:   EvaluationResult | null;
  evaluating:   boolean;
  evalError:    string | null;
  regenerating: boolean;
  regenError:   string | null;
  onEvaluate:   () => void;
  onRegenerate: (acceptedSuggestions: string, authorContext: string) => void;
}

// ── Main component ────────────────────────────────────────────────────────────

const EvaluatorPanel: React.FC<Props> = ({
  arc,
  evaluation,
  evaluating,
  evalError,
  regenerating,
  regenError,
  onEvaluate,
  onRegenerate,
}) => {
  // Channel 1: key = 'overall' | `slot-${dayNumber}`, value = accepted text
  const [accepted,  setAccepted]  = useState<Map<string, string>>(new Map());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Channel 2: author free-text enrichment context
  const [authorContext, setAuthorContext] = useState('');

  // Reset when a new evaluation arrives
  useEffect(() => {
    setAccepted(new Map());
    setDismissed(new Set());
    setAuthorContext('');
  }, [evaluation]);

  const accept = (key: string, text: string) => {
    setAccepted(prev => new Map(prev).set(key, text));
    setDismissed(prev => { const s = new Set(prev); s.delete(key); return s; });
  };

  const undoAccept = (key: string) => {
    setAccepted(prev => { const m = new Map(prev); m.delete(key); return m; });
  };

  const dismiss = (key: string) => {
    setDismissed(prev => new Set(prev).add(key));
    setAccepted(prev => { const m = new Map(prev); m.delete(key); return m; });
  };

  const undoDismiss = (key: string) => {
    setDismissed(prev => { const s = new Set(prev); s.delete(key); return s; });
  };

  const updateAccepted = (key: string, text: string) => {
    setAccepted(prev => new Map(prev).set(key, text));
  };

  const buildAcceptedSuggestions = (): string => {
    const parts: string[] = [];
    const overallText = accepted.get('overall');
    if (overallText?.trim()) parts.push(`OVERALL ARC: ${overallText.trim()}`);
    for (const [key, text] of accepted.entries()) {
      if (key === 'overall' || !text?.trim()) continue;
      const dayNum = key.replace('slot-', '');
      const slot   = arc.find(s => String(s.dayNumber) === dayNum);
      const role   = slot ? slot.role : '';
      parts.push(`Day ${dayNum}${role ? ` (${role})` : ''}: ${text.trim()}`);
    }
    return parts.join('\n');
  };

  const acceptedCount = accepted.size;
  const hasAnything   = acceptedCount > 0 || authorContext.trim().length > 0;

  return (
    <div style={{ ...card, padding: '0', overflow: 'hidden', border: '1.5px solid #EAEAEA' }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          '12px',
        padding:      '16px 20px',
        borderBottom: evaluation ? '1px solid #F0F0F0' : 'none',
        flexWrap:     'wrap',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', marginBottom: '2px' }}>
            Arc Evaluator
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {evaluation
              ? 'Review feedback below. Accept suggestions or add your own context, then regenerate.'
              : 'Let AI critique the arc and surface what would make each post stronger.'}
          </div>
        </div>

        {evaluation && <VerdictChip verdict={evaluation.verdict} />}

        {!evaluation && (
          <button
            onClick={onEvaluate}
            disabled={evaluating}
            style={{ ...secondaryBtn, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', flexShrink: 0 }}
          >
            {evaluating && <span style={spinnerStyle} />}
            {evaluating ? 'Evaluating…' : '✦ Evaluate Arc'}
          </button>
        )}
      </div>

      {/* ── Eval error ──────────────────────────────────────────────────────── */}
      {evalError && (
        <div style={{ padding: '12px 20px', color: '#C0392B', fontSize: '13px', background: '#FFF0F0' }}>
          {evalError}
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {evaluation && (
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Overall campaign feedback */}
          <SuggestionRow
            label="Campaign Arc"
            critique={evaluation.overallCritique}
            suggestion={evaluation.overallSuggestion}
            isAccepted={accepted.has('overall')}
            isDismissed={dismissed.has('overall')}
            onAccept={text => accept('overall', text)}
            onUndoAccept={() => undoAccept('overall')}
            onDismiss={() => dismiss('overall')}
            onUndoDismiss={() => undoDismiss('overall')}
            onUpdateAccepted={text => updateAccepted('overall', text)}
          />

          {/* Per-slot feedback — only weak and thin */}
          {evaluation.slots
            .filter(s => s.quality !== 'strong')
            .map((s: SlotEvaluation) => {
              const slot      = arc.find(a => a.dayNumber === s.dayNumber);
              const roleLabel = slot ? (ROLE_LABELS[slot.role] ?? slot.role) : `Day ${s.dayNumber}`;
              const key       = `slot-${s.dayNumber}`;
              return (
                <SuggestionRow
                  key={s.dayNumber}
                  label={`Day ${s.dayNumber} · ${roleLabel}`}
                  quality={s.quality}
                  critique={s.critique}
                  suggestion={s.suggestion}
                  isAccepted={accepted.has(key)}
                  isDismissed={dismissed.has(key)}
                  onAccept={text => accept(key, text)}
                  onUndoAccept={() => undoAccept(key)}
                  onDismiss={() => dismiss(key)}
                  onUndoDismiss={() => undoDismiss(key)}
                  onUpdateAccepted={text => updateAccepted(key, text)}
                />
              );
            })}

          {/* ── Enrichment section ─────────────────────────────────────────── */}
          {evaluation.enrichmentPrompt && (
            <div style={{
              background:   '#F7F3FF',
              border:       `1.5px solid ${ACCENT}22`,
              borderRadius: '10px',
              padding:      '14px 16px',
            }}>
              <div style={{
                fontSize:      '11px',
                fontWeight:    700,
                color:         ACCENT,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginBottom:  '10px',
              }}>
                Add your context{' '}
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                  (optional — strengthens the regenerated arc)
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.7', marginBottom: '12px', whiteSpace: 'pre-line' }}>
                {evaluation.enrichmentPrompt}
              </div>
              <AutoTextarea
                value={authorContext}
                onChange={setAuthorContext}
                placeholder="Share anything you have — even rough notes work…"
                minRows={3}
              />
            </div>
          )}

          {/* ── Regenerate ─────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {regenError && (
              <div style={{ color: '#C0392B', fontSize: '13px', background: '#FFF0F0', borderRadius: '8px', padding: '10px 14px' }}>
                {regenError}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => onRegenerate(buildAcceptedSuggestions(), authorContext.trim())}
                disabled={regenerating || !hasAnything}
                style={{
                  ...primaryBtn,
                  display:    'flex',
                  alignItems: 'center',
                  gap:        '7px',
                  opacity:    (!hasAnything && !regenerating) ? 0.45 : 1,
                }}
              >
                {regenerating && <span style={spinnerStyle} />}
                {regenerating ? 'Regenerating…' : 'Regenerate Arc with Feedback →'}
              </button>

              {hasAnything && !regenerating && (
                <span style={{ fontSize: '12px', color: '#888' }}>
                  {acceptedCount > 0 && `${acceptedCount} suggestion${acceptedCount > 1 ? 's' : ''} accepted`}
                  {acceptedCount > 0 && authorContext.trim() && ' + '}
                  {authorContext.trim() && 'your context added'}
                </span>
              )}

              {!hasAnything && !regenerating && (
                <span style={{ fontSize: '12px', color: '#bbb' }}>
                  Accept at least one suggestion or add your context to regenerate
                </span>
              )}
            </div>

            {!regenerating && (
              <button
                onClick={onEvaluate}
                disabled={evaluating}
                style={{
                  background: 'none', border: 'none', color: '#aaa',
                  fontSize: '12px', cursor: 'pointer', padding: '0',
                  textDecoration: 'underline', alignSelf: 'flex-start',
                }}
              >
                {evaluating ? 'Re-evaluating…' : 'Re-evaluate'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluatorPanel;
